"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { type SatelliteData } from "../components/earth-globe-r3f";

// ─── Context value ───────────────────────────────────────────────────

type EarthGlobeContextValue = {
  /** Mostrar el globo persistente */
  show: () => void;
  /** Ocultar el globo persistente */
  hide: () => void;
  /** Actualizar los datos de satélites renderizados en el globo */
  setSatellites: (_satellites: SatelliteData[]) => void;
  /** Si el globo debe ser visible */
  visible: boolean;
  /** Satélites actuales del globo */
  satellites: SatelliteData[];
  /** Índice del satélite seleccionado */
  selectedSatelliteIndex: number | null;
  /** Seleccionar un satélite o resetear a vista general */
  selectSatellite: (_index: number | null) => void;
  /** Si el globo ha sido inicializado al menos una vez */
  initialized: boolean;
  /** Ref al contenedor de estacionamiento (oculto) del canvas */
  parkingRef: React.RefObject<HTMLDivElement | null>;
  /** Ref al wrapper del canvas del globo */
  canvasWrapperRef: React.RefObject<HTMLDivElement | null>;
  /** Mover el canvas al elemento target */
  mountInto: (_target: HTMLDivElement) => void;
  /** Devolver el canvas al contenedor oculto */
  parkCanvas: () => void;
  /** Contenido de leyenda para los controles de accesibilidad */
  legendContent: ReactNode | undefined;
  /** Establecer el contenido de leyenda */
  setLegendContent: (_content: ReactNode | undefined) => void;
};

const EarthGlobeContext = createContext<EarthGlobeContextValue | null>(null);

// ─── Provider ────────────────────────────────────────────────────────

export function EarthGlobeProvider({ children }: PropsWithChildren) {
  const [visible, setVisible] = useState(false);
  const [satellites, setSatellitesState] = useState<SatelliteData[]>([]);
  const [selectedSatelliteIndex, setSelectedSatelliteIndex] = useState<
    number | null
  >(null);
  const [initialized, setInitialized] = useState(false);
  const parkingRef = useRef<HTMLDivElement>(null);
  const canvasWrapperRef = useRef<HTMLDivElement>(null);
  const [legendContent, setLegendContent] = useState<ReactNode | undefined>(
    undefined
  );

  const show = useCallback(() => {
    setVisible(true);
  }, []);
  const hide = useCallback(() => {
    setVisible(false);
  }, []);
  const setSatellites = useCallback((sats: SatelliteData[]) => {
    setSatellitesState(sats);
    if (sats.length > 0) {
      // Diferir la inicialización del canvas 3D para no bloquear el renderizado del dashboard.
      // requestIdleCallback permite que el navegador pinte y procese eventos primero.
      const schedule =
        window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 50));
      schedule(() => {
        setInitialized(true);
      });
    }
    setSelectedSatelliteIndex((currentIndex) => {
      if (currentIndex === null) return null;
      return currentIndex < sats.length ? currentIndex : null;
    });
  }, []);
  const selectSatellite = useCallback((index: number | null) => {
    setSelectedSatelliteIndex(index);
  }, []);

  const mountInto = useCallback((target: HTMLDivElement) => {
    const wrapper = canvasWrapperRef.current;
    if (wrapper && target) {
      target.appendChild(wrapper);
    }
  }, []);

  const parkCanvas = useCallback(() => {
    const wrapper = canvasWrapperRef.current;
    const parking = parkingRef.current;
    if (wrapper && parking) {
      parking.appendChild(wrapper);
    }
  }, []);

  return (
    <EarthGlobeContext.Provider
      value={{
        show,
        hide,
        setSatellites,
        visible,
        satellites,
        selectedSatelliteIndex,
        selectSatellite,
        initialized,
        parkingRef,
        canvasWrapperRef,
        mountInto,
        parkCanvas,
        legendContent,
        setLegendContent,
      }}
    >
      {children}
    </EarthGlobeContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────

export function useEarthGlobe() {
  const ctx = useContext(EarthGlobeContext);
  if (!ctx) {
    throw new Error("useEarthGlobe must be used within <EarthGlobeProvider>");
  }
  return ctx;
}
